// import React, { useState, useCallback, useRef } from 'react';
// import Cropper from 'react-easy-crop';
// import { Modal, Upload, Button, Image } from 'antd';
// import type { RcFile } from 'antd/es/upload';
// import getCroppedImg from './utils/cropImage';

// interface SignatureUploadProps {
//     onChange: (formData: FormData) => void;
//     initialImage?: string;
// }

// const SignatureUpload: React.FC<SignatureUploadProps> = ({ onChange, initialImage }) => {
//     const [imageSrc, setImageSrc] = useState<string | null>(null);
//     const [croppedImage, setCroppedImage] = useState<string | null>(initialImage || null);
//     const [crop, setCrop] = useState({ x: 0, y: 0 });
//     const [zoom, setZoom] = useState(1);
//     const [modalOpen, setModalOpen] = useState(false);
//     const croppedAreaPixelsRef = useRef<any>(null);

//     const handleFileChange = async (file: RcFile) => {
//         const reader = new FileReader();
//         reader.onload = () => {
//             setImageSrc(reader.result as string);
//             setModalOpen(true);
//         };
//         reader.readAsDataURL(file);
//         return false; // prevent default upload
//     };

//     const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
//         croppedAreaPixelsRef.current = croppedAreaPixels;
//     }, []);

//     const showCroppedImage = async () => {
//         try {
//             const croppedBlob = await getCroppedImg(imageSrc!, croppedAreaPixelsRef.current, { width: 400, height: 250 });
//             const croppedUrl = URL.createObjectURL(croppedBlob);
//             setCroppedImage(croppedUrl);
//             setModalOpen(false);

//             const formData = new FormData();
//             formData.append('file', croppedBlob, 'signature.png');
//             onChange(formData);
//         } catch (e) {
//             console.error(e);
//         }
//     };

//     return (
//         <>
//             <Upload
//                 beforeUpload={handleFileChange}
//                 showUploadList={false}
//                 accept="image/*"
//                 className='cursor-pointer'
//             >
//                 <Image
//                     src={croppedImage || 'https://placehold.co/120x60?text=Signature+Preview'}
//                     width={120}
//                     height={60}
//                     preview={false}
//                     style={{ marginTop: 16, border: '1px solid #ccc' }}
//                 />
//             </Upload>



//             <Modal
//                 open={modalOpen}
//                 onOk={showCroppedImage}
//                 onCancel={() => setModalOpen(false)}
//                 okText="Crop"
//                 title="Croper"
//                 width={600}
//                 maskClosable={false}
//             >
//                 {imageSrc && (
//                     <div style={{ position: 'relative', width: '100%', height: 400 }}>
//                         <Cropper
//                             image={imageSrc}
//                             crop={crop}
//                             zoom={zoom}
//                             aspect={4 / 2.5} // 250x400 = 3:5
//                             onCropChange={setCrop}
//                             onCropComplete={onCropComplete}
//                             onZoomChange={setZoom}
//                             cropShape="rect"
//                             showGrid={false}
//                         />
//                     </div>
//                 )}
//             </Modal>
//         </>
//     );
// };

// export default SignatureUpload;


import React, {
    useState,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle
} from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Upload, Button, Image } from 'antd';
import type { RcFile } from 'antd/es/upload';
import getCroppedImg from './utils/cropImage';

interface SignatureUploadProps {
    onChange: (formData: FormData | null) => void;
    initialImage?: string;
}

export interface SignatureUploadRef {
    reset: () => void;
}

const SignatureUpload = forwardRef<SignatureUploadRef, SignatureUploadProps>(
    ({ onChange, initialImage }, ref) => {
        const [imageSrc, setImageSrc] = useState<string | null>(null);
        const [croppedImage, setCroppedImage] = useState<string | null>(initialImage || null);
        const [crop, setCrop] = useState({ x: 0, y: 0 });
        const [zoom, setZoom] = useState(1);
        const [modalOpen, setModalOpen] = useState(false);
        const croppedAreaPixelsRef = useRef<any>(null);

        useImperativeHandle(ref, () => ({
            reset() {
                setImageSrc(null);
                setCroppedImage(null);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                croppedAreaPixelsRef.current = null;
                onChange(null);
            },
        }));

        const handleFileChange = async (file: RcFile) => {
            const reader = new FileReader();
            reader.onload = () => {
                setImageSrc(reader.result as string);
                setModalOpen(true);
            };
            reader.readAsDataURL(file);
            return false; // prevent default upload
        };

        const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
            croppedAreaPixelsRef.current = croppedAreaPixels;
        }, []);

        const showCroppedImage = async () => {
            try {
                const croppedBlob = await getCroppedImg(imageSrc!, croppedAreaPixelsRef.current, { width: 400, height: 250 });
                const croppedUrl = URL.createObjectURL(croppedBlob);
                setCroppedImage(croppedUrl);
                setModalOpen(false);

                const formData = new FormData();
                formData.append('file', croppedBlob, 'signature.png');
                onChange(formData);
            } catch (e) {
                console.error(e);
            } finally {
                setImageSrc(null);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                croppedAreaPixelsRef.current = null;
            }
        };

        return (
            <>
                <Upload
                    beforeUpload={handleFileChange}
                    showUploadList={false}
                    accept="image/*"
                    className='cursor-pointer'
                >
                    <Image
                        src={croppedImage || 'https://placehold.co/120x60?text=Signature+Preview'}
                        width={120}
                        height={60}
                        preview={false}
                        style={{ marginTop: 16, border: '1px solid #ccc' }}
                    />
                </Upload>

                <Modal
                    open={modalOpen}
                    onOk={showCroppedImage}
                    onCancel={() => {
                        setModalOpen(false);
                        setImageSrc(null);
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                        croppedAreaPixelsRef.current = null;
                    }}
                    okText="Crop"
                    title="Crop Signature"
                    width={600}
                    maskClosable={false}
                >
                    {imageSrc && (
                        <div style={{ position: 'relative', width: '100%', height: 400 }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={4 / 2.5}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                cropShape="rect"
                                showGrid={false}
                            />
                        </div>
                    )}
                </Modal>
            </>
        );
    }
);

export default SignatureUpload;
